<template>
    <div class="main">
        <c-panel :title="titulo" icon="fas fa-chart-line">
            <el-alert v-if="aviso" type="warning" :closable="false" class="u-mb-15"
                title="Como ler este relatório" :description="aviso"></el-alert>

            <el-form label-position="top">
                <el-row :gutter="10">
                    <el-col :xs="24" :sm="10">
                        <el-form-item label="Indicador">
                            <el-select v-model="filtro.configuracao_id" @change="limparResultado" filterable
                                placeholder="Selecione o indicador" style="width: 100%;">
                                <el-option v-for="c in configuracoes" :key="c.id" :label="c.nome" :value="c.id"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="8">
                        <el-form-item label="Instituição" v-if="ehRede">
                            <el-select v-model="filtro.instituicao_id" @change="carregarTurmas" filterable clearable
                                placeholder="Todas as escolas" style="width: 100%;">
                                <el-option v-for="i in instituicoes" :key="i.id" :label="i.nome" :value="i.id"></el-option>
                            </el-select>
                        </el-form-item>
                        <div class="style-form" v-else>
                            <label>Instituição</label>
                            <div class="style-input readonly">{{ nomeInstituicao }}</div>
                        </div>
                    </el-col>
                    <el-col :xs="24" :sm="6">
                        <el-form-item label="Turma (opcional)">
                            <el-select v-model="filtro.turma_id" clearable filterable placeholder="Todas as turmas" style="width: 100%;">
                                <el-option v-for="t in turmas" :key="t.id" :label="t.nome_exibicao" :value="t.id"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                </el-row>

                <el-row :gutter="10">
                    <el-col :xs="24" :sm="8">
                        <el-form-item label="Classificação de risco">
                            <el-select v-model="filtro.classificacao_risco" clearable placeholder="Todas" style="width: 100%;">
                                <el-option label="Alto" value="alto"></el-option>
                                <el-option label="Médio" value="medio"></el-option>
                                <el-option label="Baixo" value="baixo"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :xs="24" :sm="16" class="u-text-right u-mt-25">
                        <el-button size="small" icon="el-icon-search" :disabled="!filtro.configuracao_id" @click="gerar">GERAR</el-button>
                        <el-button class="u-ml-10" size="small" icon="el-icon-data-line" :disabled="!filtro.configuracao_id" @click="verEvolucao">EVOLUÇÃO</el-button>
                        <el-button class="u-ml-10" size="small" type="primary" icon="el-icon-printer" :disabled="!filtro.configuracao_id" @click="imprimir">IMPRIMIR</el-button>
                    </el-col>
                </el-row>
            </el-form>

            <div v-if="resultado">
                <el-row :gutter="10" class="u-mb-10">
                    <el-col :xs="8" :sm="6"><div class="kpi kpi-alto"><span class="n">{{ kpis.alto }}</span><span class="r">RISCO ALTO</span></div></el-col>
                    <el-col :xs="8" :sm="6"><div class="kpi kpi-medio"><span class="n">{{ kpis.medio }}</span><span class="r">RISCO MÉDIO</span></div></el-col>
                    <el-col :xs="8" :sm="6"><div class="kpi kpi-baixo"><span class="n">{{ kpis.baixo }}</span><span class="r">RISCO BAIXO</span></div></el-col>
                    <el-col :xs="24" :sm="6"><div class="kpi"><span class="n">{{ kpis.total }}</span><span class="r">ALUNOS AVALIADOS</span></div></el-col>
                </el-row>

                <el-table :data="resultado.itens || []" size="mini" border stripe empty-text="Nenhum aluno no filtro escolhido.">
                    <el-table-column prop="aluno_nome" label="Aluno" min-width="200"></el-table-column>
                    <el-table-column prop="instituicao_nome" label="Escola" min-width="160"></el-table-column>
                    <el-table-column prop="turma_nome" label="Turma" width="140"></el-table-column>
                    <el-table-column label="Risco" width="100" align="center">
                        <template slot-scope="s">
                            <el-tag size="mini" :type="corRisco(s.row.classificacao_risco)">{{ (s.row.classificacao_risco || '').toUpperCase() }}</el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="Indicador" width="100" align="center">
                        <template slot-scope="s">{{ percentual(s.row.score) }}</template>
                    </el-table-column>
                    <el-table-column label="Principais fatores" min-width="260">
                        <template slot-scope="s">
                            <span v-if="!s.row.fatores || !s.row.fatores.length" class="sem">—</span>
                            <el-tag v-for="(f, i) in (s.row.fatores || []).slice(0, 3)" :key="i" size="mini" class="fator">{{ f.rotulo }}</el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="Ficha" width="80" align="center">
                        <template slot-scope="s">
                            <el-button size="mini" icon="el-icon-view" @click="abrirFicha(s.row)"></el-button>
                        </template>
                    </el-table-column>
                </el-table>

                <el-pagination v-if="resultado.total > resultado.por_pagina" class="u-mt-10" background
                    layout="prev, pager, next" :total="resultado.total" :page-size="resultado.por_pagina"
                    :current-page="filtro.pagina" @current-change="mudarPagina"></el-pagination>
            </div>

            <el-dialog title="Evolução do risco" :visible.sync="dialogoEvolucao" width="70%" append-to-body>
                <div v-if="evolucao">
                    <el-alert v-if="evolucao.variacao" :closable="false" class="u-mb-10"
                        :type="evolucao.variacao.sentido === 'melhora' ? 'success' : (evolucao.variacao.sentido === 'piora' ? 'error' : 'info')"
                        :title="textoVariacao"></el-alert>
                    <p v-if="!evolucao.serie || evolucao.serie.length < 2" class="sem">
                        São necessárias pelo menos duas medições para desenhar a evolução. Execute o cálculo em datas diferentes.
                    </p>
                    <line-chart v-else :chart-data="dadosEvolucao" :options="opcoesEvolucao" :height="220"></line-chart>
                </div>
            </el-dialog>
        </c-panel>
    </div>
</template>

<script>
import LineChart from '@/chart/LineChart'

/**
 * BLOCO K — itens 139 e 140. Relatório de risco.
 *
 * O mesmo componente serve evasão e reprovação: muda só o `objetivo`, que filtra as
 * configurações disponíveis. Duplicar a tela deixaria duas cópias para manter.
 */
export default {
    name: 'RelatorioRisco',
    components: { LineChart },
    props: {
        objetivo: { type: String, required: true },
        titulo: { type: String, required: true }
    },
    data () {
        return {
            configuracoes: [], instituicoes: [], turmas: [],
            filtro: { configuracao_id: null, instituicao_id: null, turma_id: null, classificacao_risco: null, pagina: 1 },
            resultado: null, aviso: '', evolucao: null, dialogoEvolucao: false
        }
    },
    computed: {
        ehRede () { return [4, 5, 6, 7].includes(parseInt(this.$store.state.user.tipo)) },
        nomeInstituicao () {
            const i = this.$store.state.config.instituicao
            return i && i.nome ? i.nome : '—'
        },
        kpis () {
            const k = (this.resultado && this.resultado.kpis) || {}
            return { alto: k.alto || 0, medio: k.medio || 0, baixo: k.baixo || 0, total: k.total || 0 }
        },
        textoVariacao () {
            const v = this.evolucao.variacao
            if (!v) return ''
            const sinal = v.delta_perc_alto > 0 ? '+' : ''
            return `Risco alto: ${v.perc_alto_inicial}% na primeira medição e ${v.perc_alto_final}% na última (${sinal}${v.delta_perc_alto} p.p.).`
        },
        dadosEvolucao () {
            const s = (this.evolucao && this.evolucao.serie) || []
            return {
                labels: s.map(p => this.$moment(p.data).format('DD/MM/YY')),
                datasets: [
                    { label: '% risco alto', data: s.map(p => p.perc_alto), borderColor: '#f56c6c', backgroundColor: 'rgba(245,108,108,.1)', fill: true },
                    { label: 'Indicador médio (%)', data: s.map(p => p.media_score === null ? null : Math.round(p.media_score * 1000) / 10), borderColor: '#409eff', backgroundColor: 'rgba(64,158,255,.1)', fill: false }
                ]
            }
        },
        opcoesEvolucao () {
            return { responsive: true, maintainAspectRatio: false, scales: { yAxes: [{ ticks: { beginAtZero: true, max: 100 } }] } }
        }
    },
    mounted () {
        this.instituicoes = this.$store.state.instituicoes || []
        this.carregarConfiguracoes()
    },
    methods: {
        carregarConfiguracoes () {
            this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/preditivo/configuracao/getAll/${this.$store.state.config.periodo.id}`,
                this.$store.getters.getHeaderAuth
            ).then(r => {
                const lista = Array.isArray(r.data) ? r.data : (r.data.dados || [])
                this.configuracoes = lista.filter(c => c.objetivo === this.objetivo)
                if (this.configuracoes.length === 1) this.filtro.configuracao_id = this.configuracoes[0].id
            }).catch(e => this.$store.commit('throwException', e))
        },
        carregarTurmas () {
            this.filtro.turma_id = null
            this.turmas = []
            const inst = this.filtro.instituicao_id || (this.$store.state.config.instituicao ? this.$store.state.config.instituicao.id : null)
            if (!inst) return
            this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/turma/getAllTurmas/${inst}/${this.$store.state.config.periodo.id}`,
                this.$store.getters.getHeaderAuth
            ).then(r => {
                const lista = Array.isArray(r.data) ? r.data : Object.values(r.data || {}).flat()
                this.turmas = lista.map(t => ({ id: t.id, nome_exibicao: `${t.curso ? t.curso.nome + ' ' : ''}${t.nome}` }))
            }).catch(e => this.$store.commit('throwException', e))
        },
        limparResultado () { this.resultado = null; this.filtro.pagina = 1 },
        mudarPagina (p) { this.filtro.pagina = p; this.gerar() },
        gerar () {
            this.$axios.post(`${this.$store.state.config.baseUrlApi}/api/preditivo/listar/${this.filtro.configuracao_id}`,
                this.filtro, this.$store.getters.getHeaderAuth
            ).then(r => {
                if (r.data && r.data.erro) { window.eventBus.$emit('alert', { msg: this.textoErro(r.data.erro), type: 'warning' }); return }
                this.resultado = r.data
                this.aviso = r.data.aviso_metodologico || ''
            }).catch(e => this.$store.commit('throwException', e))
        },
        verEvolucao () {
            this.$axios.post(`${this.$store.state.config.baseUrlApi}/api/preditivo/relatorio/evolucao/${this.filtro.configuracao_id}`,
                this.filtro, this.$store.getters.getHeaderAuth
            ).then(r => {
                if (r.data && r.data.erro) { window.eventBus.$emit('alert', { msg: this.textoErro(r.data.erro), type: 'warning' }); return }
                this.evolucao = r.data
                this.dialogoEvolucao = true
            }).catch(e => this.$store.commit('throwException', e))
        },
        abrirFicha (linha) {
            this.$router.push(`/admin/preditivo/aluno?configuracao_id=${this.filtro.configuracao_id}&matricula_id=${linha.matricula_id}`)
        },
        imprimir () {
            window.eventBus.$emit('print', {
                url: `api/preditivo/imprimirRelatorio/${this.filtro.configuracao_id}`,
                param: this.filtro
            })
        },
        corRisco (c) { return c === 'alto' ? 'danger' : (c === 'medio' ? 'warning' : 'success') },
        percentual (s) { return s === null || s === undefined ? '—' : `${Math.round(s * 1000) / 10}%` },
        textoErro (e) { return typeof e === 'string' ? e : (e.text || 'Não foi possível gerar o relatório.') }
    }
}
</script>
<style scoped>
.kpi { border: 1px solid #dcdfe6; border-radius: 4px; padding: 10px; text-align: center; margin-bottom: 8px; }
.kpi .n { display: block; font-size: 20px; font-weight: bold; }
.kpi .r { display: block; font-size: 11px; color: #606266; }
.kpi-alto { border-left: 4px solid #f56c6c; }
.kpi-medio { border-left: 4px solid #e6a23c; }
.kpi-baixo { border-left: 4px solid #67c23a; }
.fator { margin-right: 4px; }
.sem { color: #909399; font-size: 12px; }
</style>
